import {
  createFeature,
  createReducer,
  createSelector,
  on,
  provideState,
} from '@ngrx/store';
import { LiveChatActions } from './live-chat.actions';
import { WindowStateActions } from '../window/window.actions';
import { makeEnvironmentProviders } from '@angular/core';
import { provideEffects } from '@ngrx/effects';
import { LiveChatEffects } from './live-chat.effects';
import { LiveChatEndReason } from '../../types/enum';
import { vaSessionFeature } from '../va-session/va-session.reducer';

export const initialState: Readonly<LiveChat> = {
  isConnected: false,
  receivedFirstOperatorMessage: false,
  endReason: LiveChatEndReason.EMPTY,
  webSocketURL: '',
  queueApiKey: '',
  chatId: '',
  chatKey: '',
  operatorName: '',
  operatorId: '',
  clientId: '',
  position: undefined,
  unreadMessageCount: 0,
  safeSendUrl: '',
  departmentId: '',
  platform: '',
  createChatType: '',
  aaEligible: false,
  isAASessionCreated: false,
  isTransferBetweenPlatforms: false,
};

export const liveChatFeature = createFeature({
  name: 'liveChat',
  reducer: createReducer(
    initialState,
    on(LiveChatActions.setConnection, (state) => {
      return { ...state, isConnected: true };
    }),
    on(LiveChatActions.setChatId, (state, { value }) => {
      return { ...state, chatId: value };
    }),
    on(LiveChatActions.assignedToFirstOperator, (state) => {
      return { ...state, receivedFirstOperatorMessage: true };
    }),
    on(LiveChatActions.endLiveChat, (state) => {
      return { ...state, isConnected: false };
    }),
    on(
      LiveChatActions.setLiveChatState,
      (state, { chatId, chatKey, clientId, webSocketURL, queueApiKey }) => {
        return {
          ...state,
          chatId,
          chatKey,
          clientId,
          webSocketURL,
          queueApiKey,
        };
      }
    ),
    on(
      LiveChatActions.resetOperator,
      LiveChatActions.waitingForAnAgent,
      (state) => {
        return { ...state, operatorName: '', operatorId: '' };
      }
    ),
    on(LiveChatActions.setOperator, (state, { operatorName, operatorId }) => {
      return { ...state, operatorName, operatorId };
    }),
    on(LiveChatActions.setEndReason, (state, { reason }) => {
      return { ...state, endReason: reason };
    }),
    on(LiveChatActions.incrementUnreadCount, (state) => {
      return { ...state, unreadMessageCount: state.unreadMessageCount + 1 };
    }),
    on(WindowStateActions.openWindow, (state) => {
      return { ...state, unreadMessageCount: 0 };
    }),
    on(LiveChatActions.setSafeSendUrl, (state, { safeSendUrl }) => {
      return { ...state, safeSendUrl };
    }),
    on(
      LiveChatActions.escalateToLiveChat,
      (state, { departmentId, platform, aaEligible }) => {
        return { ...state, departmentId, platform, aaEligible };
      }
    ),
    on(LiveChatActions.getCreateChatResponse, (state, { createChatType }) => {
      return { ...state, createChatType };
    }),
    on(
      LiveChatActions.setAgentAssistSession,
      (state, { isAASessionCreated }) => {
        return { ...state, isAASessionCreated };
      }
    ),
    on(
      LiveChatActions.transferBetweenPlatforms,
      (state, { targetPlatform }) => {
        return {
          ...state,
          platform: targetPlatform,
          isConnected: false,
          isTransferBetweenPlatforms: true,
        };
      }
    ),
    on(LiveChatActions.setDepartmentId, (state, { departmentId }) => {
      return { ...state, departmentId };
    })
  ),
});

export const selectIsConnectedAndIsLiveChat = createSelector(
  liveChatFeature.selectIsConnected,
  liveChatFeature.selectReceivedFirstOperatorMessage,
  (isConnected, receivedFirstOperatorMessage) => {
    return [isConnected, receivedFirstOperatorMessage];
  }
);

export const selectWebSocketParam = createSelector(
  liveChatFeature.selectWebSocketURL,
  liveChatFeature.selectChatKey,
  liveChatFeature.selectReceivedFirstOperatorMessage,
  (
    websocketURL: string,
    chatKey: string,
    receivedFirstOperatorMessage: boolean
  ) => {
    return { websocketURL, chatKey, receivedFirstOperatorMessage };
  }
);

export const selectAASendMessageParams = createSelector(
  liveChatFeature.selectChatId,
  vaSessionFeature.selectSessionIdentifier,
  liveChatFeature.selectOperatorId,
  liveChatFeature.selectIsAASessionCreated,
  liveChatFeature.selectDepartmentId,
  (
    chatId: string,
    sessionId: string,
    operatorId: string,
    isAASessionCreated: boolean,
    departmentId: string
  ) => {
    return { chatId, sessionId, operatorId, isAASessionCreated, departmentId };
  }
);

export const {
  selectLiveChatState,
  selectChatId,
  selectChatKey,
  selectIsConnected,
  selectReceivedFirstOperatorMessage,
  selectOperatorName,
  selectOperatorId,
  selectPosition,
  selectClientId,
  selectQueueApiKey,
  selectEndReason,
  selectWebSocketURL,
  selectSafeSendUrl,
  selectUnreadMessageCount,
  selectDepartmentId,
  selectPlatform,
  selectCreateChatType,
  selectAaEligible,
  selectIsAASessionCreated,
  selectIsTransferBetweenPlatforms,
} = liveChatFeature;

export function provideLiveChatFeature() {
  return makeEnvironmentProviders([
    provideState(liveChatFeature),
    provideEffects(LiveChatEffects),
  ]);
}
